import React, { useEffect, useRef, useState, useCallback } from "react";
import Highcharts from "highcharts";
import { useQuery } from "@tanstack/react-query";
import { fetchClientMonthlyRace } from "../../services/api/analytics";

const CURRENT_YEAR = new Date().getFullYear();

const ClientYearTrendChart = ({ filterParams = {} }) => {
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const { data: result, isFetching } = useQuery({
    queryKey: ["analytics-clientMonthlyRace", CURRENT_YEAR, filterParams.clientId],
    queryFn: () => fetchClientMonthlyRace({ year: CURRENT_YEAR, ...filterParams }),
    staleTime: 60_000,
  });

  const raceData = result?.data;

  const toggleSeries = useCallback((clientId, seriesIndex) => {
    const chart = chartRef.current;
    if (!chart) return;
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
        chart.series[seriesIndex]?.setVisible(true, true);
      } else {
        next.add(clientId);
        chart.series[seriesIndex]?.setVisible(false, true);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!containerRef.current || !raceData?.series?.length) return;

    chartRef.current?.destroy();
    chartRef.current = null;

    const { series, categories } = raceData;
    const maxY = Math.max(...series.flatMap((s) => s.data), 1);

    chartRef.current = Highcharts.chart(containerRef.current, {
      credits: { enabled: false },
      accessibility: { enabled: false },
      chart: {
        type: "spline",
        height: 400,
        style: { fontFamily: "Poppins, sans-serif" },
        backgroundColor: "transparent",
        animation: { duration: 600 },
      },
      title: { text: null },
      xAxis: {
        categories,
        lineColor: "#f1f5f9",
        tickLength: 0,
        labels: { style: { fontSize: "10px", color: "#94a3b8" } },
        crosshair: { color: "#e2e8f0", width: 1 },
      },
      yAxis: {
        title: { text: null },
        min: 0,
        max: maxY + Math.ceil(maxY * 0.15),
        tickInterval: 10,
        allowDecimals: false,
        gridLineColor: "#f1f5f9",
        gridLineDashStyle: "Dash",
        labels: { style: { fontSize: "10px", color: "#94a3b8" } },
      },
      legend: { enabled: false },
      tooltip: {
        shared: false,
        useHTML: true,
        backgroundColor: "#1e293b",
        borderWidth: 0,
        borderRadius: 10,
        shadow: false,
        style: { color: "#f1f5f9", fontSize: "12px", padding: "10px" },
        formatter() {
          const meta = this.series.userOptions?.meta ?? {};
          const avatar = meta.logoUrl
            ? `<img src="${meta.logoUrl}" style="width:14px;height:14px;border-radius:50%;object-fit:cover;vertical-align:middle;margin-right:4px;">`
            : `<span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:50%;background:${this.series.color};font-size:6px;font-weight:700;color:#fff;vertical-align:middle;margin-right:4px;">${meta.initials ?? ""}</span>`;
          return `<div style="min-width:240px"><div style="font-size:10px;color:#94a3b8;margin-bottom:4px">${this.point.category}</div><div style="display:flex;align-items:center;gap:4px;">${avatar}<span style="color:#cbd5e1">${this.series.name}:</span> <b style="color:#fff">${this.y}</b></div></div>`;
        },
      },
      plotOptions: {
        spline: {
          lineWidth: 2.5,
          marker: { enabled: true, radius: 3, symbol: "circle", lineWidth: 1.5, lineColor: null },
          states: { hover: { lineWidth: 3.5 } },
        },
      },
      series: series.map((s) => ({
        name: s.name,
        color: s.color,
        data: s.data,
        meta: { logoUrl: s.logoUrl, initials: s.initials },
      })),
    });

    requestAnimationFrame(() => chartRef.current?.reflow());

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [raceData]);

  const hasData = (raceData?.series ?? []).length > 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-800 text-sm">Client Trend — {CURRENT_YEAR}</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Monthly work orders per client · full year view
        </p>
      </div>

      {isFetching && (
        <div className="flex items-center justify-center py-10">
          <i className="fa-solid fa-circle-notch fa-spin text-primary text-lg" />
        </div>
      )}

      {!isFetching && !hasData && (
        <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
          No work order data for {CURRENT_YEAR}
        </div>
      )}

      {hasData && !isFetching && (
        <>
          <div ref={containerRef} style={{ width: "100%" }} />

          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 px-1">
            {raceData.series.map((s, i) => {
              const hidden = hiddenSeries.has(s.clientId);
              return (
                <button
                  key={s.clientId}
                  onClick={() => toggleSeries(s.clientId, i)}
                  className={`flex items-center gap-1.5 text-[11px] font-medium transition-all ${
                    hidden ? "opacity-40" : "opacity-100"
                  }`}
                >
                  {s.logoUrl ? (
                    <img
                      src={s.logoUrl}
                      alt={s.name}
                      className={`w-4 h-4 rounded-full object-cover flex-shrink-0 ${hidden ? "grayscale" : ""}`}
                    />
                  ) : (
                    <span
                      className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                      style={{ fontSize: 7, backgroundColor: hidden ? "#d1d5db" : s.color }}
                    >
                      {s.initials}
                    </span>
                  )}
                  <span className="text-gray-600">{s.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientYearTrendChart;
